/**
 * Shared classification and user-facing recovery surface for the direct Parse
 * session flow.
 *
 * Both the Auth0 callback (`/~auth`) and the protected route bootstrap
 * (`/router/middleware`) must tell apart two very different failure domains:
 *
 * - A business/identity rejection: the account cannot access Giotto and no
 *   retry will change that. The only safe recovery is ending the identity.
 * - A transient failure: the account is fine, the service is not reachable.
 *   A deliberate retry is offered without discarding a valid Parse token.
 *
 * Keeping the wording and the kind list here avoids the two boundaries drifting
 * apart while staying free of any session/token access.
 */
import Swal from 'sweetalert2';
import {
  ParseSessionError,
  PARSE_SESSION_ERROR_KIND,
} from './parseSessionError';

export const BUSINESS_REJECTION_KINDS = [
  PARSE_SESSION_ERROR_KIND.UNSUPPORTED_IDENTITY,
  PARSE_SESSION_ERROR_KIND.INVALID_CREDENTIALS,
  PARSE_SESSION_ERROR_KIND.UNKNOWN_IDENTITY,
  PARSE_SESSION_ERROR_KIND.ACCOUNT_CONFLICT,
  PARSE_SESSION_ERROR_KIND.BINDING_CONFLICT,
];

export const isBusinessRejection = (error) =>
  error instanceof ParseSessionError &&
  BUSINESS_REJECTION_KINDS.includes(error.kind);

// An Auth0 SDK failure (missing/expired session, rejected token exchange) is
// not a ParseSessionError but carries the OAuth `error` code. The session
// module reports the same condition - the Auth0 token cache cannot provide
// the identity credentials - as a ParseSessionError with the AUTH0_SESSION
// kind. Both can only be recovered by restarting the authorization, never by
// retrying Parse.
export const isAuth0SessionError = (error) => {
  if (error instanceof ParseSessionError) {
    return error.kind === PARSE_SESSION_ERROR_KIND.AUTH0_SESSION;
  }

  return (
    !!error && typeof error === 'object' && typeof error.error === 'string'
  );
};

export const showAccessErrorModal = ({ business = false } = {}) =>
  Swal.fire({
    icon: 'error',
    title: 'Accesso non riuscito',
    text: business
      ? 'Questo account non può accedere a Giotto. Verifica l’invito con l’amministratore o contatta l’assistenza.'
      : 'Il servizio non è disponibile. Puoi riprovare senza uscire dal tuo account.',
    confirmButtonText: business ? 'Ho capito' : 'Riprova',
    showCancelButton: true,
    cancelButtonText: 'Cambia account',
    allowOutsideClick: false,
    allowEscapeKey: false,
  });
